import { FC, lazy, useMemo, Suspense, useCallback, useEffect, useRef, ComponentType } from 'react';
import { Endpoints, IAddProjectCoverContent, Project, Team } from '@brg/api';
import { Colors, FlexBox, useIsMobile, useScale, FixedFooterWrapper } from '@brg/ui';
import styled, { createGlobalStyle, useTheme } from 'styled-components';
import { useTranslation } from "react-i18next";
import { ApplicationShared } from "../../../../core";
import { getHasSpecialSourcesBlocks } from "../../../helpers/getHasSpecialSourcesBlocks";
import { ICoverContent } from 'shared/pages/exploreHseContextsNew/const/coverContent';
import { IVotesContent } from 'shared/pages/exploreHseContextsNew/const/votesContent';
import { LoaderSpinner } from "../../../modals/artzLegalsModal/LoaderAnimation";
import { AppTester } from "../../../../supporting";
import { HseContextActions } from "../../../reducers/hseCurrentContext";
import { useDispatch, useSelector } from "react-redux";
import { CurrentProjectSelector } from "../../../reducers/CurrentProject";
import { selectorLMSModuleInfo } from "../../../reducers/LMSSlice";
import { ProjectDelegate } from "project-delegate";
import {useGetPodcastLongreadProps} from "../../../components/AudioPlayer/hooks/useGetPodcastLongreadProps";
import { getProjectHrefPrefix } from 'shared/pages/projectEdit/support/getProjectHrefPrefix';
import { navigationHandler } from 'shared/pages/projectEdit/support/navigationHandler';
import { executeLmsButtonPath } from 'shared/utils/executeLmsButtonPath';
import useQueryParams from 'shared/hooks/useQueryParams';
import { isPromptModule } from 'shared/pages/exploreHseContextsNew/const/promptFormsId';
import { useGetExternalLGHandlers } from 'shared/hooks/useGetExternalLGHandlers';
import { BookType } from '_deziiign/books/constants';
import useChapterPosts from '_deziiign/books/pages/BookEdit/hooks/useChapterPosts';
import { isOuterCourseTeam, ModerationType } from 'shared/pages/teams/const/ModerationTypes';
import { useHistory, useLocation } from 'react-router-dom';
import { useProfile } from '@brg/auth';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { BookDelegate } from '_deziiign/books/core/book-delegate';

const LongRead = lazy(() =>
  import('brg-longread').then((module) => ({ default: module.LongRead as ComponentType<any> })),
);

const LONGREAD_PAGING_LIMIT = 20;

type PropsT = {
  project: Project & Partial<Team<'Local'>>;
  onZoom?: (src: string) => void;
  onViewCurrentSlideScroll?: any;
  setMetaTags?: (val: string[]) => void;
  isTeamLG?: boolean;
  coverContent?: ICoverContent;
  votesContent?: IVotesContent;
  additionalContent?: IAddProjectCoverContent;
  disableVoting?: boolean;
  isThemeLongread?: boolean;
  customLGId?: string;
  themeIndex?: number;
  setPollTime?: (v: any, isFromVoteClick?: boolean) => void;
  onCheckHomework?: () => void;
  uncheckedHomeWorkCount?: number;
  onSetThemeAnchors?: (e: any) => void;
  onApplyExam?: (e: any) => void;
  lastLongreadBlockLink?: string;
  courseGeneralColor?: string;
  teamId?: string;
  courseButtons: any[];
  lmsLongreadOrder?: string[];
  projectSource?: any;
  protectInfoComponent?: React.ReactNode;
  limitContentCount?: number;
  pagingLimit?: number;
  lmsModuleInfo?: any;
  isPromtWeekendModule?: boolean;
  showScheduleBlocks?: boolean;
  moduleScheduleLessons?: any[];
  isModuleLessonsLoading?: boolean;
  themeIdAliases?: string[];
  onEditLesson?: (lesson: any) => void;
  onRegisterConsultation?: (lesson: any) => void;
  onViewConsultationRecords?: (lesson: any) => void;
  hasPurchaseButton?: boolean;
  externalPostBlocks?: any[];
};

const LongreadWrapper = styled(FlexBox)`
  font-family: "HSE Sans";

  img {
    cursor: unset !important;
  }

  a {
    &[href^="/tag/"] {
      text-decoration: none !important;
    }
  }
`

const RegularFooterStyle = createGlobalStyle`
  body {
    ${FixedFooterWrapper} {
      //position: relative;
    }
  }
`

export const getLongreadSign = (project: any, lang: any) => {
  if (project?.isBilingual) {
    if (lang === 'en' && project?.longreadId_en) {
      return project?.longreadEnSign
    }
    if (lang === 'ru' && project?.longreadId) {
      return project?.longreadSign
    }
    return project?.longreadSign || project?.longreadEnSign;
  }
  return project?.longreadSign;
}
export const getLongreadId = (project: any, lang: any) => {
  if (project?.bookType === BookType.playbook) {
    return project?.projectId;
  }
  if (project?.isBilingual) {
    if (lang === 'en' && project?.longreadId_en) {
      return project?.longreadId_en
    }
    if (lang === 'ru' && project?.longreadId) {
      return project?.longreadId
    }
    return project?.longreadId || project?.longreadId_en;
  }
  return project?.longreadId;
}

export const getTeamLgId = (team: any, lang: any) => {
  if (!!team?.isBilingual) {
    if (lang === 'en' && team?.longreadId_en) {
      return team?.longreadId_en
    }
    if (lang === 'ru' && team?.longreadId) {
      return team?.longreadId
    }
    return team?.longreadId || team?.longreadId_en;
  }

  return team?.longreadId
}
 

export const PSLongread: FC<PropsT> = ({
  project,
  onZoom,
  onViewCurrentSlideScroll,
  setMetaTags,
  isTeamLG,
  coverContent,
  votesContent,
  additionalContent,
  disableVoting,
  setPollTime,
  customLGId,
  projectSource,
  onCheckHomework,
  uncheckedHomeWorkCount,
  lastLongreadBlockLink,
  onSetThemeAnchors,
  courseGeneralColor,
  isThemeLongread,
  teamId,
  courseButtons,
  lmsLongreadOrder,
  themeIndex,
  onApplyExam,
  protectInfoComponent,
  limitContentCount,
  pagingLimit,
  lmsModuleInfo,
  isPromtWeekendModule,
  showScheduleBlocks,
  moduleScheduleLessons,
  isModuleLessonsLoading,
  themeIdAliases,
  onEditLesson,
  onRegisterConsultation,
  onViewConsultationRecords,
  hasPurchaseButton,
  externalPostBlocks,
}) => {
  const s = useScale();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const profile = useProfile();
  const dispatch = useDispatch();
  const showOriginals = useSelector(CurrentProjectSelector)?.showOriginals;
  const isOstbay = useSelector(CurrentProjectSelector)?.isOstbay
  const podcastProps = useGetPodcastLongreadProps(project?.projectId)
  const { printType } = useQueryParams();
  const { hash } = useLocation();

  const externalPosts = useChapterPosts(project);

  const isLMSProject = useMemo(() => {
    return project?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item))
  }, [project])

  const reduxLmsModuleInfo = useSelector(selectorLMSModuleInfo)
  const moduleInfo = lmsModuleInfo || reduxLmsModuleInfo

  const canEditModule = useMemo(() => {
    if (!isLMSProject || isThemeLongread || !profile?.accountId) return false
    const isAgeyTomesh = AppTester.isAgeytomesh()
    const isMe =
      project?.author?.accountId === profile.accountId ||
      project?.coAuthors?.some((author: any) => author?.accountId === profile.accountId)
    const isCreator = !!project?.creatorId && project.creatorId === profile.accountId
    const isModuleTeacher =
      !!moduleInfo?.teacherIds?.includes(profile.accountId) &&
      moduleInfo?.moduleProjectId === project?.projectId
    return (
      !isAgeyTomesh &&
      (isCreator || isMe || isModuleTeacher || ProjectDelegate.isProjectEditableFor(profile, project)) &&
      !BookDelegate?.isPaid(project)
    )
  }, [isLMSProject, isThemeLongread, profile, moduleInfo, project])

  const onEditModule = useCallback(() => {
    const moduleId = project?.projectId
    if (!moduleId) return
    if (AppTester.isLMSHSE() || AppTester.isLMSHSECreative()) {
      history.push(CreateLinkTo.projectEdit(moduleId))
      return
    }
    const lmsUrl = window.location.origin.includes('com')
      ? process.env.REACT_APP_URL_HSEDESIGN_LMS_COM
      : process.env.REACT_APP_URL_HSEDESIGN_LMS
    const baseUrl = lmsUrl?.endsWith('/') ? lmsUrl.slice(0, -1) : lmsUrl
    if (baseUrl) {
      window.open(`${baseUrl}/edit/${moduleId}`)
      return
    }
    history.push(CreateLinkTo.projectEdit(moduleId))
  }, [history, project?.projectId])

  const isOuterCourse = useMemo(() => isOuterCourseTeam(project), [project]);

  const shouldHavePagination = !hash;

  const teamModerationType = useMemo(() => {
    // @ts-ignore — moderationType exists on LMS project payloads, not on Project<"Local"> typings
    return project?.moderationType || null
  }, [project])

  const noGap = useMemo(() => {
    // @ts-ignore
    return project?.longread?.additionalInfo?.[0]?.rowGap === 0;
  }, [project])

  const moduleContentLink = useMemo(() => {
    // @ts-ignore
    return project?.additionalInfo?.contentLink;
  }, [project])

  const moduleColor = useMemo(() => {
    return project?.additionalInfo?.moduleColor || null
  }, [project])
  
  const isDarkTheme = theme.projectView.titleColor === Colors.galBlack100;

  const moduleCoverUrl = useMemo(() => {
    const images: any[] = (project as any)?.titleImages || [];
    const img = images.find((f: any) => f.size === 'original') || images[0];
    return img?.url ? `${Endpoints.files}/${img.url}` : undefined;
  }, [(project as any)?.titleImages]);

  const longread = customLGId ? customLGId : isTeamLG ? getTeamLgId(project, i18n.language) : getLongreadId(project, i18n.language);
  const longreadSign = getLongreadSign(project, i18n.language);

  const externalHandlers = useGetExternalLGHandlers({
    raw: project,
    id: project?.projectId,
    isTeamLg: isTeamLG,
    longreadId: longread,
  });

  const viewType = !!customLGId ? projectSource?.longread?.viewType : (project?.longread?.viewType || 'container');
  const hasSpecialSourcesBlocks = useMemo(() => (
    getHasSpecialSourcesBlocks(project?.tagsID ? project?.tagsID?.map(tag => tag) : [])
  ), [project?.tagsID])

  const styles = useMemo(() => ({
    maxWidth: isMobile || ['fluid', 't-shape', 'fixed-ratio-large', 'free-type'].includes(String(viewType)) ? '100%' : s(1152) + 'px',
    width: '100%',
    margin: '0 auto',
  }), [isMobile, s, viewType]);

  const titleIsHidden = useMemo(() => {
    if (project?.longreadType) {
      return projectSource?.isTitleHidden
    }
    if (additionalContent && additionalContent?.title?.text?.length > 0) {
      return !additionalContent?.title?.visible
    }
    return project?.isTitleHidden
  }, [project?.longreadType, projectSource?.isTitleHidden, additionalContent]) as boolean

  const isWideLongread = ['fixed-ratio-large', 't-shape', 'free-type'].includes(String(viewType));

  const scrollToAnchor = useCallback((anchor: string) => {
    const anchorElement = document.getElementById(anchor);
    if (anchorElement) {
      setTimeout(() => {
        anchorElement.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [])

  const scrolledHashRef = useRef<string | null>(null);

  const onSetAnchors = useCallback((anchors: any) => {
      if (hash && typeof hash === 'string' && !isThemeLongread && scrolledHashRef.current !== hash) {
        scrolledHashRef.current = hash;
        scrollToAnchor(hash.replace('#', ''));
      }
      //@ts-ignore
      const { visibleAnchorsLimit: limit } = project || {};
      if (anchors?.length) {
        dispatch(HseContextActions.setAnchorLinks({ anchors, limit: isLMSProject ? 3 : limit, override: isLMSProject }))
      }
  },[dispatch, isLMSProject, hash, isThemeLongread, scrollToAnchor])

  useEffect(() => {
    return () => {
      dispatch(HseContextActions.resetAnchorLinks())
    }
  }, [dispatch])

// @ts-ignore
const detailsPath = executeLmsButtonPath(project?.teams?.[0]?.buttons);

  return (
    <Suspense fallback={
      <FlexBox justify="center" align="center" width='100%' style={{ height: 200 }}>
        <LoaderSpinner color={theme.selectContextModal.loaderText} text=" "/>
      </FlexBox>
    }>
      <LongreadWrapper style={styles} itemProp="articleBody">
        {isWideLongread && <RegularFooterStyle/>}
        <LongRead
          colorStyle={isDarkTheme ? 'light' : 'grey'}
          moduleColor={moduleColor}
          printType={printType}
          areaId=""
          isEditable={false}
          countryId=""
          type=""
          longreadId={longread}
          projectId={project?.projectId}
          author={project?.author}
          linkTo={'geography'}
          isLMS={isLMSProject}
          //@ts-ignore
          courseRoute={project?.teams?.[0]?.route} 
          //@ts-ignore
          contentLink={moduleContentLink} 
          teamModerationType={teamModerationType}
          detailsPath={detailsPath}
          themeIndex={themeIndex}
          courseButtons={courseButtons}
          //@ts-ignore
          lmsButtonLabel={project?.additionalInfo?.buttonLabel} 
          onOverlayedImageClick={(originalImage: any) => {
            onZoom?.(originalImage);
          }}
          viewType={viewType}
          theme={theme}
          appName={ApplicationShared.application}
          onViewCurrentSlideScroll={onViewCurrentSlideScroll}
          lang={i18n.language}
          disableVoting={disableVoting}
          setMetaTags={setMetaTags}
          hasSpecialSourcesBlocks={hasSpecialSourcesBlocks}
          includedCards={project?.includedCards || []}
          coverContent={{ ...coverContent }}
          votesContent={votesContent}
          protectInfoComponent={protectInfoComponent}
          limitContentCount={limitContentCount}
          additionalContent={additionalContent}
          //временный костыль для теста
          typeConnection={(process.env.REACT_APP_API_CONNECTION_MODE === 'develop' || process.env.REACT_APP_API_CONNECTION_MODE === 'staging') ? 'develop' : process.env.REACT_APP_API_CONNECTION_MODE}
          //@ts-ignore
          titleIsHidden={titleIsHidden}
          hseFonts={false}
          setUpdateVoteTime={setPollTime}
          onSetAnchors={onSetAnchors}
          showOriginals={showOriginals}
          onCheckHomework={onCheckHomework}
          uncheckedHomeWorkCount={uncheckedHomeWorkCount}
          lastLongreadBlockLink={lastLongreadBlockLink}
          courseGeneralColor={courseGeneralColor}
          moduleCoverUrl={moduleCoverUrl}
          onSetThemeAnchors={onSetThemeAnchors}
          sign={longreadSign}
          teamId={teamId}
          podcastProps={podcastProps}
          lmsLongreadOrder={lmsLongreadOrder}
          onApplyExam={onApplyExam}
          title={project?.title}
          noGap={noGap}
          pagingLimit={shouldHavePagination ? pagingLimit : 300}
          frontendPaging
          isPromptModule={isPromptModule(project?.projectId)}
          isPromtWeekendModule={isPromtWeekendModule}
          isOstbay={isOstbay}
          lmsModuleInfo={lmsModuleInfo}
          onEditModule={canEditModule ? onEditModule : undefined}
          editModuleLabel={canEditModule ? t('module_edit.edit') : undefined}
          onEditLesson={onEditLesson}
          onRegisterConsultation={onRegisterConsultation}
          onViewConsultationRecords={onViewConsultationRecords}
          showScheduleBlocks={showScheduleBlocks}
          moduleScheduleLessons={moduleScheduleLessons}
          isModuleLessonsLoading={isModuleLessonsLoading}
          themeIdAliases={themeIdAliases}
          hasPurchaseButton={hasPurchaseButton}
          externalPostBlocks={externalPosts}
          specBlockProps={{ getProjectHrefPrefix, navigationHandler }}
            //@ts-ignore
          titleNewPosition={project?.longreadType ? projectSource?.additionalInfo?.titlePosition : project?.additionalInfo?.titlePosition }
          isOuterCourse={isOuterCourse}
          {...externalHandlers}
        />
      </LongreadWrapper>
    </Suspense>
  );
};
import { FC, lazy, useMemo, Suspense, useCallback, useEffect, useRef, ComponentType } from 'react';
import { Endpoints, IAddProjectCoverContent, Project, Team } from '@brg/api';
import { Colors, FlexBox, useIsMobile, useScale, FixedFooterWrapper } from '@brg/ui';
import styled, { createGlobalStyle, useTheme } from 'styled-components';
import { useTranslation } from "react-i18next";
import { ApplicationShared } from "../../../../core";
import { getHasSpecialSourcesBlocks } from "../../../helpers/getHasSpecialSourcesBlocks";
import { ICoverContent } from 'shared/pages/exploreHseContextsNew/const/coverContent';
import { IVotesContent } from 'shared/pages/exploreHseContextsNew/const/votesContent';
import { LoaderSpinner } from "../../../modals/artzLegalsModal/LoaderAnimation";
import { AppTester } from "../../../../supporting";
import { HseContextActions } from "../../../reducers/hseCurrentContext";
import { useDispatch, useSelector } from "react-redux";
import { CurrentProjectSelector } from "../../../reducers/CurrentProject";
import { selectorLMSModuleInfo } from "../../../reducers/LMSSlice";
import { ProjectDelegate } from "project-delegate";
import {useGetPodcastLongreadProps} from "../../../components/AudioPlayer/hooks/useGetPodcastLongreadProps";
import { getProjectHrefPrefix } from 'shared/pages/projectEdit/support/getProjectHrefPrefix';
import { navigationHandler } from 'shared/pages/projectEdit/support/navigationHandler';
import { executeLmsButtonPath } from 'shared/utils/executeLmsButtonPath';
import useQueryParams from 'shared/hooks/useQueryParams';
import { isPromptModule } from 'shared/pages/exploreHseContextsNew/const/promptFormsId';
import { useGetExternalLGHandlers } from 'shared/hooks/useGetExternalLGHandlers';
import { BookType } from '_deziiign/books/constants';
import useChapterPosts from '_deziiign/books/pages/BookEdit/hooks/useChapterPosts';
import { isOuterCourseTeam, ModerationType } from 'shared/pages/teams/const/ModerationTypes';
import { useHistory, useLocation } from 'react-router-dom';
import { useProfile } from '@brg/auth';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { BookDelegate } from '_deziiign/books/core/book-delegate';

const LongRead = lazy(() =>
  import('brg-longread').then((module) => ({ default: module.LongRead as ComponentType<any> })),
);

const LONGREAD_PAGING_LIMIT = 20;

type PropsT = {
  project: Project & Partial<Team<'Local'>>;
  onZoom?: (src: string) => void;
  onViewCurrentSlideScroll?: any;
  setMetaTags?: (val: string[]) => void;
  isTeamLG?: boolean;
  coverContent?: ICoverContent;
  votesContent?: IVotesContent;
  additionalContent?: IAddProjectCoverContent;
  disableVoting?: boolean;
  isThemeLongread?: boolean;
  customLGId?: string;
  themeIndex?: number;
  setPollTime?: (v: any, isFromVoteClick?: boolean) => void;
  onCheckHomework?: () => void;
  uncheckedHomeWorkCount?: number;
  onSetThemeAnchors?: (e: any) => void;
  onApplyExam?: (e: any) => void;
  lastLongreadBlockLink?: string;
  courseGeneralColor?: string;
  teamId?: string;
  courseButtons: any[];
  lmsLongreadOrder?: string[];
  projectSource?: any;
  protectInfoComponent?: React.ReactNode;
  limitContentCount?: number;
  pagingLimit?: number;
  lmsModuleInfo?: any;
  isPromtWeekendModule?: boolean;
  showScheduleBlocks?: boolean;
  moduleScheduleLessons?: any[];
  isModuleLessonsLoading?: boolean;
  themeIdAliases?: string[];
  onEditLesson?: (lesson: any) => void;
  onRegisterConsultation?: (lesson: any) => void;
  onViewConsultationRecords?: (lesson: any) => void;
  hasPurchaseButton?: boolean;
  externalPostBlocks?: any[];
};

const LongreadWrapper = styled(FlexBox)`
  font-family: "HSE Sans";

  img {
    cursor: unset !important;
  }

  a {
    &[href^="/tag/"] {
      text-decoration: none !important;
    }
  }
`

const RegularFooterStyle = createGlobalStyle`
  body {
    ${FixedFooterWrapper} {
      //position: relative;
    }
  }
`

export const getLongreadSign = (project: any, lang: any) => {
  if (project?.isBilingual) {
    if (lang === 'en' && project?.longreadId_en) {
      return project?.longreadEnSign
    }
    if (lang === 'ru' && project?.longreadId) {
      return project?.longreadSign
    }
    return project?.longreadSign || project?.longreadEnSign;
  }
  return project?.longreadSign;
}
export const getLongreadId = (project: any, lang: any) => {
  if (project?.bookType === BookType.playbook) {
    return project?.projectId;
  }
  if (project?.isBilingual) {
    if (lang === 'en' && project?.longreadId_en) {
      return project?.longreadId_en
    }
    if (lang === 'ru' && project?.longreadId) {
      return project?.longreadId
    }
    return project?.longreadId || project?.longreadId_en;
  }
  return project?.longreadId;
}

export const getTeamLgId = (team: any, lang: any) => {
  if (!!team?.isBilingual) {
    if (lang === 'en' && team?.longreadId_en) {
      return team?.longreadId_en
    }
    if (lang === 'ru' && team?.longreadId) {
      return team?.longreadId
    }
    return team?.longreadId || team?.longreadId_en;
  }

  return team?.longreadId
}
 

export const PSLongread: FC<PropsT> = ({
  project,
  onZoom,
  onViewCurrentSlideScroll,
  setMetaTags,
  isTeamLG,
  coverContent,
  votesContent,
  additionalContent,
  disableVoting,
  setPollTime,
  customLGId,
  projectSource,
  onCheckHomework,
  uncheckedHomeWorkCount,
  lastLongreadBlockLink,
  onSetThemeAnchors,
  courseGeneralColor,
  isThemeLongread,
  teamId,
  courseButtons,
  lmsLongreadOrder,
  themeIndex,
  onApplyExam,
  protectInfoComponent,
  limitContentCount,
  pagingLimit,
  lmsModuleInfo,
  isPromtWeekendModule,
  showScheduleBlocks,
  moduleScheduleLessons,
  isModuleLessonsLoading,
  themeIdAliases,
  onEditLesson,
  onRegisterConsultation,
  onViewConsultationRecords,
  hasPurchaseButton,
  externalPostBlocks,
}) => {
  const s = useScale();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const profile = useProfile();
  const dispatch = useDispatch();
  const showOriginals = useSelector(CurrentProjectSelector)?.showOriginals;
  const isOstbay = useSelector(CurrentProjectSelector)?.isOstbay
  const podcastProps = useGetPodcastLongreadProps(project?.projectId)
  const { printType } = useQueryParams();
  const { hash } = useLocation();

  const externalPosts = useChapterPosts(project);

  const isLMSProject = useMemo(() => {
    return project?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item))
  }, [project])

  const reduxLmsModuleInfo = useSelector(selectorLMSModuleInfo)
  const moduleInfo = lmsModuleInfo || reduxLmsModuleInfo

  const canEditModule = useMemo(() => {
    if (!isLMSProject || isThemeLongread || !profile?.accountId) return false
    const isAgeyTomesh = AppTester.isAgeytomesh()
    const isMe =
      project?.author?.accountId === profile.accountId ||
      project?.coAuthors?.some((author: any) => author?.accountId === profile.accountId)
    const isCreator = !!project?.creatorId && project.creatorId === profile.accountId
    const isModuleTeacher =
      !!moduleInfo?.teacherIds?.includes(profile.accountId) &&
      moduleInfo?.moduleProjectId === project?.projectId
    return (
      !isAgeyTomesh &&
      (isCreator || isMe || isModuleTeacher || ProjectDelegate.isProjectEditableFor(profile, project)) &&
      !BookDelegate?.isPaid(project)
    )
  }, [isLMSProject, isThemeLongread, profile, moduleInfo, project])

  const onEditModule = useCallback(() => {
    const moduleId = project?.projectId
    if (!moduleId) return
    if (AppTester.isLMSHSE() || AppTester.isLMSHSECreative()) {
      history.push(CreateLinkTo.projectEdit(moduleId))
      return
    }
    const lmsUrl = window.location.origin.includes('com')
      ? process.env.REACT_APP_URL_HSEDESIGN_LMS_COM
      : process.env.REACT_APP_URL_HSEDESIGN_LMS
    const baseUrl = lmsUrl?.endsWith('/') ? lmsUrl.slice(0, -1) : lmsUrl
    if (baseUrl) {
      window.open(`${baseUrl}/edit/${moduleId}`)
      return
    }
    history.push(CreateLinkTo.projectEdit(moduleId))
  }, [history, project?.projectId])

  const isOuterCourse = useMemo(() => isOuterCourseTeam(project), [project]);

  const shouldHavePagination = !hash;

  const teamModerationType = useMemo(() => {
    // @ts-ignore — moderationType exists on LMS project payloads, not on Project<"Local"> typings
    return project?.moderationType || null
  }, [project])

  const noGap = useMemo(() => {
    // @ts-ignore
    return project?.longread?.additionalInfo?.[0]?.rowGap === 0;
  }, [project])

  const moduleContentLink = useMemo(() => {
    // @ts-ignore
    return project?.additionalInfo?.contentLink;
  }, [project])

  const moduleColor = useMemo(() => {
    return project?.additionalInfo?.moduleColor || null
  }, [project])
  
  const isDarkTheme = theme.projectView.titleColor === Colors.galBlack100;

  const moduleCoverUrl = useMemo(() => {
    const images: any[] = (project as any)?.titleImages || [];
    const img = images.find((f: any) => f.size === 'original') || images[0];
    return img?.url ? `${Endpoints.files}/${img.url}` : undefined;
  }, [(project as any)?.titleImages]);

  const longread = customLGId ? customLGId : isTeamLG ? getTeamLgId(project, i18n.language) : getLongreadId(project, i18n.language);
  const longreadSign = getLongreadSign(project, i18n.language);

  const externalHandlers = useGetExternalLGHandlers({
    raw: project,
    id: project?.projectId,
    isTeamLg: isTeamLG,
    longreadId: longread,
  });

  const viewType = !!customLGId ? projectSource?.longread?.viewType : (project?.longread?.viewType || 'container');
  const hasSpecialSourcesBlocks = useMemo(() => (
    getHasSpecialSourcesBlocks(project?.tagsID ? project?.tagsID?.map(tag => tag) : [])
  ), [project?.tagsID])

  const styles = useMemo(() => ({
    maxWidth: isMobile || ['fluid', 't-shape', 'fixed-ratio-large', 'free-type'].includes(String(viewType)) ? '100%' : s(1152) + 'px',
    width: '100%',
    margin: '0 auto',
  }), [isMobile, s, viewType]);

  const titleIsHidden = useMemo(() => {
    if (project?.longreadType) {
      return projectSource?.isTitleHidden
    }
    if (additionalContent && additionalContent?.title?.text?.length > 0) {
      return !additionalContent?.title?.visible
    }
    return project?.isTitleHidden
  }, [project?.longreadType, projectSource?.isTitleHidden, additionalContent]) as boolean

  const isWideLongread = ['fixed-ratio-large', 't-shape', 'free-type'].includes(String(viewType));

  const scrollToAnchor = useCallback((anchor: string) => {
    const anchorElement = document.getElementById(anchor);
    if (anchorElement) {
      setTimeout(() => {
        anchorElement.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [])

  const scrolledHashRef = useRef<string | null>(null);

  const onSetAnchors = useCallback((anchors: any) => {
      if (hash && typeof hash === 'string' && !isThemeLongread && scrolledHashRef.current !== hash) {
        scrolledHashRef.current = hash;
        scrollToAnchor(hash.replace('#', ''));
      }
      //@ts-ignore
      const { visibleAnchorsLimit: limit } = project || {};
      if (anchors?.length) {
        dispatch(HseContextActions.setAnchorLinks({ anchors, limit: isLMSProject ? 3 : limit, override: isLMSProject }))
      }
  },[dispatch, isLMSProject, hash, isThemeLongread, scrollToAnchor])

  useEffect(() => {
    return () => {
      dispatch(HseContextActions.resetAnchorLinks())
    }
  }, [dispatch])

// @ts-ignore
const detailsPath = executeLmsButtonPath(project?.teams?.[0]?.buttons);

  return (
    <Suspense fallback={
      <FlexBox justify="center" align="center" width='100%' style={{ height: 200 }}>
        <LoaderSpinner color={theme.selectContextModal.loaderText} text=" "/>
      </FlexBox>
    }>
      <LongreadWrapper style={styles} itemProp="articleBody">
        {isWideLongread && <RegularFooterStyle/>}
        <LongRead
          colorStyle={isDarkTheme ? 'light' : 'grey'}
          moduleColor={moduleColor}
          printType={printType}
          areaId=""
          isEditable={false}
          countryId=""
          type=""
          longreadId={longread}
          projectId={project?.projectId}
          author={project?.author}
          linkTo={'geography'}
          isLMS={isLMSProject}
          //@ts-ignore
          courseRoute={project?.teams?.[0]?.route} 
          //@ts-ignore
          contentLink={moduleContentLink} 
          teamModerationType={teamModerationType}
          detailsPath={detailsPath}
          themeIndex={themeIndex}
          courseButtons={courseButtons}
          //@ts-ignore
          lmsButtonLabel={project?.additionalInfo?.buttonLabel} 
          onOverlayedImageClick={(originalImage: any) => {
            onZoom?.(originalImage);
          }}
          viewType={viewType}
          theme={theme}
          appName={ApplicationShared.application}
          onViewCurrentSlideScroll={onViewCurrentSlideScroll}
          lang={i18n.language}
          disableVoting={disableVoting}
          setMetaTags={setMetaTags}
          hasSpecialSourcesBlocks={hasSpecialSourcesBlocks}
          includedCards={project?.includedCards || []}
          coverContent={{ ...coverContent }}
          votesContent={votesContent}
          protectInfoComponent={protectInfoComponent}
          limitContentCount={limitContentCount}
          additionalContent={additionalContent}
          //временный костыль для теста
          typeConnection={(process.env.REACT_APP_API_CONNECTION_MODE === 'develop' || process.env.REACT_APP_API_CONNECTION_MODE === 'staging') ? 'develop' : process.env.REACT_APP_API_CONNECTION_MODE}
          //@ts-ignore
          titleIsHidden={titleIsHidden}
          hseFonts={false}
          setUpdateVoteTime={setPollTime}
          onSetAnchors={onSetAnchors}
          showOriginals={showOriginals}
          onCheckHomework={onCheckHomework}
          uncheckedHomeWorkCount={uncheckedHomeWorkCount}
          lastLongreadBlockLink={lastLongreadBlockLink}
          courseGeneralColor={courseGeneralColor}
          moduleCoverUrl={moduleCoverUrl}
          onSetThemeAnchors={onSetThemeAnchors}
          sign={longreadSign}
          teamId={teamId}
          podcastProps={podcastProps}
          lmsLongreadOrder={lmsLongreadOrder}
          onApplyExam={onApplyExam}
          title={project?.title}
          noGap={noGap}
          pagingLimit={shouldHavePagination ? pagingLimit : 300}
          frontendPaging
          isPromptModule={isPromptModule(project?.projectId)}
          isPromtWeekendModule={isPromtWeekendModule}
          isOstbay={isOstbay}
          lmsModuleInfo={lmsModuleInfo}
          onEditModule={canEditModule ? onEditModule : undefined}
          editModuleLabel={canEditModule ? t('module_edit.edit') : undefined}
          onEditLesson={onEditLesson}
          onRegisterConsultation={onRegisterConsultation}
          onViewConsultationRecords={onViewConsultationRecords}
          showScheduleBlocks={showScheduleBlocks}
          moduleScheduleLessons={moduleScheduleLessons}
          isModuleLessonsLoading={isModuleLessonsLoading}
          themeIdAliases={themeIdAliases}
          hasPurchaseButton={hasPurchaseButton}
          externalPostBlocks={externalPosts}
          specBlockProps={{ getProjectHrefPrefix, navigationHandler }}
            //@ts-ignore
          titleNewPosition={project?.longreadType ? projectSource?.additionalInfo?.titlePosition : project?.additionalInfo?.titlePosition }
          isOuterCourse={isOuterCourse}
          {...externalHandlers}
        />
      </LongreadWrapper>
    </Suspense>
  );
};
